import { AlertTriangleIcon, CheckCircle2Icon } from "lucide-react";
import type { ReactNode } from "react";
import { useNavigate, useParams } from "react-router";

import { PortalLayout } from "@/components/portal/PortalLayout";
import { LIMITE_EN_PALABRAS, SeccionDocumentos } from "@/components/portal/SeccionDocumentos";
import { SeccionFacturacion } from "@/components/portal/SeccionFacturacion";
import { Spinner } from "@/components/ui/spinner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { usePortal } from "@/hooks/usePortalPublico";
import { ApiError } from "@/lib/api";

/** Las dos secciones. El valor es el que aparece en la URL. */
type Seccion = "documentos" | "facturacion";

/**
 * El portal al que llega EL CLIENTE desde los dos botones del correo de
 * aceptación.
 *
 * SIN LOGIN. Es la tercera pantalla del sistema que no lo tiene —además de las
 * de autenticación— junto con `AceptarCotizacion` y `VerificarCotizacion`. El
 * cliente no tiene cuenta en Consultek y nunca la va a tener: su credencial es
 * el token de la URL.
 *
 * ══ POR QUÉ LA SECCIÓN VA EN LA URL Y NO EN UN `useState` ═══════════════════
 *
 * Porque el correo trae DOS botones que apuntan a `/portal/{token}` y a
 * `/portal/{token}/facturacion`, y cada uno tiene que caer donde promete. Con
 * la sección en estado local, el botón de facturación abriría la pestaña de
 * documentos y quien lo apretó —contabilidad, que no es quien junta los
 * papeles— tendría que buscar la suya. Ese clic de más es lo que el segundo
 * botón existe para evitar.
 *
 * Además hace que la pestaña sobreviva a recargar y a compartir el enlace
 * adentro de la empresa del cliente, que es exactamente lo que va a pasar.
 *
 * ══ NO NOMBRA NADA INTERNO ══════════════════════════════════════════════════
 *
 * No dice "informe", no menciona estados del proceso ni roles, y no muestra el
 * precio —que el cliente ya tiene en el PDF que firmó—. Del otro lado hay
 * alguien que pidió una tasación, no un usuario del sistema. El backend tampoco
 * manda esos campos: son las dos mitades de la misma decisión.
 *
 * ══ LOS DOS RECHAZOS TIENEN PANTALLAS DISTINTAS ═════════════════════════════
 *
 * - **410**: el encargo se cerró. Quien vuelve meses después tiene que leer "ya
 *   terminamos", no un error, o va a creer que se perdió todo lo que subió.
 * - **404**: el enlace no es válido — típicamente un token cortado al copiarlo
 *   desde el correo.
 *
 * No hay 409 acá, al revés que en la aceptación: este enlace no se quema y no
 * vence, así que no existe el caso intermedio.
 */
export function PortalDelCliente({ seccion = "documentos" }: { seccion?: Seccion }) {
  const { token } = useParams<{ token: string }>();
  const navegar = useNavigate();

  const consulta = usePortal(token);

  // Enlace sin token: ni vale la pena pegarle a la API.
  if (!token) {
    return (
      <Estado
        icon={<AlertTriangleIcon className="size-6" />}
        titulo="Enlace no válido"
        descripcion="Este enlace está incompleto. Revisa que lo hayas copiado entero desde el correo, o responde ese correo y te ayudamos."
      />
    );
  }

  if (consulta.isLoading) {
    return (
      <PortalLayout>
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <Spinner className="text-primary size-6" />
          <p className="text-ink-muted text-caption">Abriendo tu portal…</p>
        </div>
      </PortalLayout>
    );
  }

  if (consulta.isError) {
    const status = consulta.error instanceof ApiError ? consulta.error.status : null;

    if (status === 410) {
      return (
        <Estado
          icon={<CheckCircle2Icon className="size-6" />}
          titulo="Este encargo ya está cerrado"
          descripcion="El trabajo se entregó, así que ya no necesitamos más antecedentes. Si necesitas ayuda, responde el correo que te enviamos."
        />
      );
    }

    return (
      <Estado
        icon={<AlertTriangleIcon className="size-6" />}
        titulo="Enlace no válido"
        descripcion="No pudimos abrir tu portal. Revisa que hayas copiado el enlace entero desde el correo, o responde ese correo y te ayudamos."
      />
    );
  }

  if (!consulta.data) {
    // Estado transitorio, justo antes de que `isLoading` refleje el fetch en
    // curso: mismo criterio que `AceptarCotizacion`, no reventar contra
    // `undefined`.
    return (
      <PortalLayout>
        <div className="flex justify-center py-16">
          <Spinner className="text-primary size-6" />
        </div>
      </PortalLayout>
    );
  }

  const portal = consulta.data;
  // Cuántos archivos mandó en total. Antes contaba los documentos que faltaban;
  // sin una lista de categorías que cumplir, "lo que falta" ya no se puede
  // calcular sin adivinar, y un número que se inventa es peor que ninguno.
  const enviados = portal.secciones.reduce((suma, s) => suma + s.archivos.length, 0);

  return (
    <PortalLayout>
      {/* Cabecera. El nombre de la propiedad es lo dominante: es lo que le dice
          al cliente cuál de sus encargos es este. */}
      <header className="mb-7">
        <h1 className="text-h1 text-ink">{portal.propiedad ?? "Tu tasación"}</h1>

        <p className="text-ink-muted text-caption mt-1">
          {[portal.cliente, portal.ubicacion].filter(Boolean).join(" · ")}
        </p>

        {/* La frase NO puede empezar con "para el": `entrega` es casi siempre
            un PLAZO ("15 días hábiles a contar de la visita de inspección") y
            solo en las cotizaciones viejas un día del calendario. Un molde que
            asuma una fecha imprime "para el 15 días hábiles…" en la primera
            pantalla que ve el cliente. El plazo pelado tampoco se repite al
            lado: ya viene dentro de `entrega`. */}
        {portal.entrega ? (
          <p className="text-ink-body mt-4 text-sm text-pretty">
            Entrega: <span className="text-ink font-bold">{portal.entrega}</span>. Mientras
            antes tengamos los antecedentes, antes salimos a terreno.
          </p>
        ) : null}
      </header>

      <Tabs
        variant="underline"
        value={seccion}
        onValueChange={(valor) =>
          // `replace` para no llenar el historial: alternar entre las dos
          // secciones no es navegación que el cliente quiera deshacer con el
          // botón "atrás" cinco veces.
          navegar(valor === "facturacion" ? `/portal/${token}/facturacion` : `/portal/${token}`, {
            replace: true,
          })
        }
      >
        <TabsList>
          <TabsTrigger value="documentos">
            Documentación
            {/* El contador va en el disparador y no dentro de la sección: la
                pregunta "¿ya mandé algo?" se responde ANTES de entrar. */}
            {enviados > 0 ? (
              <span className="text-ink-hint ml-1.5 font-mono text-xs">{enviados}</span>
            ) : null}
          </TabsTrigger>
          <TabsTrigger value="facturacion">Datos de facturación</TabsTrigger>
        </TabsList>

        <TabsContent value="documentos" className="mt-6">
          <SeccionDocumentos
            token={token}
            secciones={portal.secciones}
            documentosPedidos={portal.documentos_pedidos}
          />

          <p className="text-ink-hint mt-4 text-xs text-pretty">
            {/* Se nombran las FAMILIAS y no las dieciséis extensiones: lo lee
                alguien que está mirando su escritorio. Y ya no dice solo "PDF,
                JPG, PNG o WEBP" — desde que cada archivo produce su versión
                para la IA, el expediente también acepta Office, justamente para
                que el cliente no tenga que convertir nada antes de mandarlo. */}
            PDF, documentos de Office (Word, Excel, PowerPoint) o imágenes (JPG, PNG, WEBP),
            hasta {LIMITE_EN_PALABRAS} por archivo. Puedes subir varios a la vez y volver cuando
            quieras: se guardan a medida que los mandas. Si alguno no está disponible o no
            aplica a tu caso, avísanos igualmente.
          </p>
        </TabsContent>

        <TabsContent value="facturacion" className="mt-6">
          <SeccionFacturacion token={token} facturacion={portal.facturacion} />
        </TabsContent>
      </Tabs>

      {/* El ejecutivo, con sus DOS canales. Dice "Habla con" y no "Escríbele"
          porque desde que hay teléfono ya no se ofrece solo escribir. El
          teléfono es OPCIONAL en la ficha del usuario: cuando falta, esta
          línea queda exactamente como estaba, sin un separador colgando ni un
          rótulo vacío. */}
      <footer className="text-ink-muted mt-10 border-t pt-6 text-sm text-pretty">
        ¿Alguna duda? Habla con {portal.contacto.nombre}:{" "}
        <a href={`mailto:${portal.contacto.email}`} className="text-primary font-semibold underline">
          {portal.contacto.email}
        </a>
        {portal.contacto.telefono ? (
          <>
            {" · "}
            <Telefono numero={portal.contacto.telefono} />
          </>
        ) : null}
      </footer>
    </PortalLayout>
  );
}

/**
 * El teléfono del ejecutivo, como enlace `tel:` cuando se puede marcar.
 *
 * ══ POR QUÉ NO SIEMPRE ES UN ENLACE ═════════════════════════════════════════
 *
 * El campo es texto libre a propósito: acá entran anexos, prefijos y
 * separadores que cada persona escribe distinto, y un formato inventado
 * rechazaría números que funcionan. Pero "+56 2 2345 6789 anexo 12" no se
 * convierte en un `tel:` sin mentir — sacarle lo que no es dígito produce un
 * número al que el cliente llamaría y no llegaría a ninguna parte, y en un
 * teléfono eso no falla ruidosamente: alguien atiende, y es otra persona.
 *
 * Así que solo se enlaza lo que es un número Y NADA MÁS. El resto se imprime
 * tal cual, que es lo que el cliente necesita para marcarlo a mano.
 */
function Telefono({ numero }: { numero: string }) {
  const marcable = /^\+?[\d\s().-]+$/.test(numero);

  if (!marcable) {
    return <span className="text-ink font-semibold">{numero}</span>;
  }

  return (
    <a
      href={`tel:${numero.replace(/[\s().-]/g, "")}`}
      className="text-primary font-semibold underline"
    >
      {numero}
    </a>
  );
}

/**
 * Pantalla de una sola pieza: ícono, título y explicación.
 *
 * NO lleva botón, igual que su gemela de `AceptarCotizacion` y por lo mismo: el
 * destino natural sería "iniciar sesión", y el lector no tiene cuenta. Un
 * enlace al login sería mandar a un cliente a una puerta que no puede abrir. La
 * salida es responder el correo, y eso lo dice cada descripción.
 */
function Estado({
  icon,
  titulo,
  descripcion,
}: {
  icon: ReactNode;
  titulo: string;
  descripcion: string;
}) {
  return (
    <PortalLayout>
      <div className="mx-auto max-w-md py-12 text-center">
        <div className="bg-accent text-accent-foreground mx-auto mb-5 flex size-12 items-center justify-center rounded-2xl">
          {icon}
        </div>
        <h1 className="text-h2 text-ink">{titulo}</h1>
        <p className="text-ink-muted text-caption mt-2 text-pretty">{descripcion}</p>
      </div>
    </PortalLayout>
  );
}
